import { template as template_c16cb5d08da14e5db07b769f53433276 } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isBlank } from '@ember/utils';
import { fn, hash } from '@ember/helper';
import SearchInput from 'eflex/components/inputs/search';
import { or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import PortalTarget from 'ember-stargate/components/portal-target';
import BsButton from 'eflex/components/bs-button';
import BsForm from 'eflex/components/bs-form';
import DateRangeFilter from 'eflex/components/filters/date-range';
import ImageTypeFilter from 'eflex/components/filters/image-type';
import LogLevelFilter from 'eflex/components/filters/log-level';
import PassFailFilter from 'eflex/components/filters/pass-fail';
import TextFieldFilter from 'eflex/components/filters/text-field';
import PlantFilter from 'eflex/components/filters/plant-filter';
import FastenerFilter from 'eflex/components/bi/bi-panel/fasteners';
import ProcessDataSearchFilter from 'eflex/components/bi/bi-panel/process-data-search';
import ModelFilter from 'eflex/components/bi/bi-panel/models';
import UserFilter from 'eflex/components/bi/bi-panel/users';
import TaskTagFilter from 'eflex/components/bi/bi-panel/task-tags';
import ProcessDataFilter from 'eflex/components/bi/bi-panel/process-data';
import StateFilter from 'eflex/components/bi/bi-panel/states';
import ShiftFilter from 'eflex/components/bi/bi-panel/shifts';
import StationStatusFilter from 'eflex/components/bi/bi-panel/station-statuses';
import UniqueSerialFilter from 'eflex/components/bi/bi-panel/unique-serial';
import SingleStationFilter from 'eflex/components/bi/bi-panel/location';
import SidePanel from 'eflex/components/side-panel';
import TimeSince from 'eflex/components/time-since';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { service } from '@ember/service';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { forEachObjIndexed, mergeLeft } from 'ramda';
import moment from 'moment-timezone';
import { triggerEventBus } from 'eflex/helpers';
export default class FilterParameters extends Component {
    @service
    intl;
    @service
    store;
    @tracked
    params = {};
    @tracked
    initParams = {};
    @tracked
    storedParams = {};
    @tracked
    showPanel = false;
    @tracked
    isInvalid = false;
    @tracked
    lastSearchedMoment = moment();
    constructor(){
        super(...arguments);
        this.initParam('searchTerm', this.args.searchTerm);
        this.initParam('searchType', this.args.searchType);
    }
    initParam = (key, value)=>{
        if (isBlank(value)) {
            this.params[key] = null;
        } else {
            this.storedParams[key] = value;
            this.params[key] = value;
        }
    };
    updateParam = (key, value)=>{
        this.params = {
            ...this.params,
            [key]: value
        };
    };
    updateStoredParam = (key, value)=>{
        this.params = {
            ...this.params,
            [key]: value
        };
        this.storedParams = {
            ...this.storedParams,
            [key]: value
        };
        this.args.onParamChange(key, value);
    };
    updateSearchParam = (key, value)=>{
        this.params = {
            ...this.storedParams,
            [key]: value
        };
    };
    setStoredParams = ()=>{
        this.storedParams = {
            ...this.storedParams
        };
    };
    getParam = (key)=>{
        return this.params[key];
    };
    onSearch = ()=>{
        if (this.isInvalid) {
            return;
        }
        this.showPanel = false;
        forEachObjIndexed((value, key)=>{
            if (isBlank(value)) {
                delete this.storedParams[key];
            } else {
                this.storedParams[key] = value;
            }
        }, this.params);
        Object.keys(this.params).forEach((key)=>{
            this.args.onParamChange?.(key, this.params[key]);
        });
        this.args.onSearch({
            ...this.storedParams
        });
        this.setStoredParams();
        this.setLastSearched();
    };
    createSearchBadgeText = (paramKey, translationKeyForSingle, translationKeyForMultiple, textField = null, modelType = null)=>{
        const length = this.storedParams[paramKey]?.length ?? 0;
        if (length === 0) {
            return '';
        }
        if (length === 1) {
            const paramValue = modelType ? this.store.peekRecord(modelType, this.storedParams[paramKey][0]) : this.storedParams[paramKey][0];
            if (!paramValue) {
                return;
            }
            const valueText = textField ? paramValue[textField] : paramValue;
            return `${this.intl.t(translationKeyForSingle)}: ${valueText}`;
        }
        return this.intl.t(translationKeyForMultiple, {
            length
        });
    };
    get hasFilters() {
        const paramsToIgnore = [
            'searchType'
        ];
        if (this.args.isForBi) {
            paramsToIgnore.push('useTotalCycles', 'isNumericSearch');
        }
        const paramsToTrack = Object.keys(this.storedParams).filter((key)=>!paramsToIgnore.includes(key));
        return paramsToTrack.length > 0;
    }
    setLastSearched() {
        this.lastSearchedMoment = moment();
    }
    togglePanel = ()=>{
        this.showPanel = !this.showPanel;
        if (this.showPanel) {
            this.params = mergeLeft(this.storedParams, this.params);
        } else {
            forEachObjIndexed((value, key)=>{
                this.params[key] = this.storedParams[key];
            }, this.params);
        }
    };
    clearAll = ()=>{
        const fieldsToRetain = new Set([
            'searchType'
        ]);
        Object.keys(this.params).forEach((key)=>{
            if (!fieldsToRetain.has(key)) {
                this.updateParam(key, null);
            }
        });
        this.args?.clearAll?.();
        this.onSearch();
    };
    static{
        template_c16cb5d08da14e5db07b769f53433276(`
    <div
      class="component-filter-parameters"
      {{didInsert this.setStoredParams}}
      ...attributes
    >
      <div class="search-wrapper mb-3">
        <div class="row gx-2 align-items-center w-100 justify-content-between">
          {{#unless @isForBi}}
            <div class={{or @searchWrapClass "col-7"}}>
              <div class="w-100">
                <SearchInput
                  @inputClass={{@inputClass}}
                  @searchTerm={{this.params.searchTerm}}
                  @searchType={{this.params.searchType}}
                  @placeholder={{@placeholder}}
                  @onSubmit={{this.onSearch}}
                  @onInput={{fn this.updateSearchParam 'searchTerm'}}>
                  {{yield this.updateStoredParam this.params.searchType to='searchPrefix'}}
                </SearchInput>
              </div>
            </div>
          {{/unless}}

          {{#if @isForBi}}
            <div class="col-auto">
              <BsButton @type='secondary' class="test-csv-export me-1" @onClick={{triggerEventBus 'exportToCsv'}}>
                {{t 'exportCsv'}}
              </BsButton>
              <BsButton @type='secondary' class="test-refresher me-1" @onClick={{@refresh}}>
                {{t 'refresh'}}
              </BsButton>
              {{#if @autoRefreshes}}
                <span class="test-time-since fs-4">{{t "automaticallyRefreshesEveryMinute"}}</span>
              {{else}}
                <TimeSince @labelPrefix={{t "lastSearch"}} @lastEvent={{this.lastSearchedMoment}} />
              {{/if}}
            </div>
          {{/if}}

          <div class="col-auto">
            <BsButton
              class="filter-btn d-flex align-items-center fs-4"
              @size="{{if @isForBi "xl"}}sm"
              @onClick={{this.togglePanel}}
            >
              <FaIcon @icon="sliders" @prefix="fas" @size="xs" class="me-2" />
              {{t "filters"}}
            </BsButton>
          </div>

          {{#unless @isForBi}}
            {{yield this.params to="rightOfAdvancedSearch"}}
          {{/unless}}
        </div>

        <div class="search-tags d-flex align-items-center mt-1">
          {{#if this.hasFilters}}
            <span class="fw-semibold fs-4 me-1">
              {{t "controls.visor.resultsfor"}}:
            </span>
          {{/if}}
          {{#if @searchTerm}}
            <span class="fs-4 me-1">"{{@searchTerm}}"</span>
          {{/if}}

          <PortalTarget @name="search-badge-tags" @multiple={{true}} />

          {{#if this.hasFilters}}
            <BsButton
              @type="link"
              @size="sm"
              class="clear-all link-danger text-decoration-none fw-semibold p-0 px-1 ms-1"
              @onClick={{this.clearAll}}
            >
              {{t "filters.clearfilters"}}
            </BsButton>
          {{/if}}
        </div>
      </div>

      <BsForm as |form|>
        {{#let (fn (mut this.isInvalid)) as |setIsInvalid|}}
          {{yield
            (hash
              DateRange=(component
                DateRangeFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                setIsInvalid=setIsInvalid
              )
              ImageType=(component
                ImageTypeFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              LogLevel=(component
                LogLevelFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              ModelFilter=(component
                ModelFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                createSearchBadgeText=this.createSearchBadgeText
              )
              PassFail=(component
                PassFailFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              ProcessDataFilter=(component
                ProcessDataFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                createSearchBadgeText=this.createSearchBadgeText
              )
              ShiftFilter=(component
                ShiftFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                createSearchBadgeText=this.createSearchBadgeText
              )
              SingleStationFilter=(component
                SingleStationFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              StateFilter=(component
                StateFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                createSearchBadgeText=this.createSearchBadgeText
              )
              StationStatusFilter=(component
                StationStatusFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              TaskTagFilter=(component
                TaskTagFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                createSearchBadgeText=this.createSearchBadgeText
              )
              TextField=(component
                TextFieldFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              TreeFilter=(component
                PlantFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              UniqueSerialFilter=(component
                UniqueSerialFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              UserFilter=(component
                UserFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                createSearchBadgeText=this.createSearchBadgeText
              )
              FastenerFilter=(component
                FastenerFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
              )
              ProcessDataSearchFilter=(component
                ProcessDataSearchFilter
                form=form
                initParam=this.initParam
                updateParam=this.updateParam
                getParam=this.getParam
                createSearchBadgeText=this.createSearchBadgeText
              )
            )
          }}
        {{/let}}
      </BsForm>

      {{#if this.showPanel}}
        <SidePanel
          @size="lg"
          @header={{t "filters"}}
          @subheader={{@page}}
          @onClose={{this.togglePanel}}
        >
          <PortalTarget class="flex-fill bg-gray-100" @name="filter-fields" @multiple={{true}} />

          <div class="filters-panel d-flex align-items-center bg-gray-100 w-100 justify-content-end p-3 sticky-bottom">
            <div class="row align-items-center g-2">
              <div class="col-auto">
                <BsButton
                  class="cancel-filters"
                  @onClick={{this.togglePanel}}
                >
                  {{t "cancel"}}
                </BsButton>
              </div>
              <div class="col-auto">
                <BsButton
                  class="{{if this.isInvalid "disabled"}} apply-filters"
                  @type="primary"
                  @onClick={{this.onSearch}}
                >
                  {{t "apply"}}
                </BsButton>
              </div>
            </div>
          </div>
        </SidePanel>
      {{/if}}
    </div>

    {{yield this.storedParams to="belowSearch"}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
